// ============================================
// Gold Standard Calibration Cases
// 10 verified synthetic patients with known biomarker
// values and formulary-expected recommendations.
//
// Each case maps specific biomarker values to the
// products whose triggers they satisfy, per the
// seed formulary trigger definitions.
// ============================================

import type { BiomarkerResult } from '../../types';
import type { GoldStandardCase } from '../../types/calibrationRun';
import { BIOMARKER_RANGES } from '../biomarkerRanges';

// ── Helper: build a BiomarkerResult from name + value ──

function b(
  name: string,
  value: number,
  gender: 'female' | 'male' | 'any' = 'any',
): BiomarkerResult {
  const range = BIOMARKER_RANGES.find(
    (r) => r.name === name && (r.gender === 'any' || r.gender === gender),
  );
  if (!range) {
    throw new Error(`Biomarker range not found: ${name} (${gender})`);
  }

  const withinStandard = value >= range.standardMin && value <= range.standardMax;
  const withinFunctional = value >= range.functionalMin && value <= range.functionalMax;

  let hemologixStatus: BiomarkerResult['hemologixStatus'] = 'optimal';
  if (!withinFunctional) {
    if (value < range.functionalMin) {
      hemologixStatus = value < range.standardMin ? 'critical' : 'low';
    } else {
      hemologixStatus = value > range.standardMax ? 'critical' : 'elevated';
    }
    if (withinStandard && !withinFunctional) {
      hemologixStatus = 'suboptimal';
    }
  }

  return {
    biomarker: name,
    value,
    unit: range.unit,
    standardRange: { low: range.standardMin, high: range.standardMax },
    functionalRange: { low: range.functionalMin, high: range.functionalMax },
    eliteAverage: range.eliteAverage,
    standardStatus: withinStandard ? 'normal' : 'abnormal',
    hemologixStatus,
    category: range.category,
    interpretation: `${name} at ${value} ${range.unit} — ${hemologixStatus} (functional range: ${range.functionalMin}-${range.functionalMax})`,
  };
}

// ── The 10 Gold Standard Cases ──

export const GOLD_STANDARD_CASES: GoldStandardCase[] = [
  // ─── Case 1: Classic female nutritional deficiency ───
  {
    caseId: 'gs-001-chas',
    name: 'Chas — Classic Female Nutritional',
    description: 'Tired 45F, doctor says "normal." Low D, B12, iron, magnesium, elevated inflammation.',
    demographics: { age: 45, sex: 'female' },
    goals: ['energy', 'immune'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('Vitamin D (25-OH)', 28),
      b('B12', 310),
      b('Ferritin', 22, 'female'),
      b('Magnesium (RBC)', 4.8),
      b('hs-CRP', 1.8),
      b('TSH', 1.5),
      b('Fasting Glucose', 82),
      b('Hemoglobin A1c', 5.0),
      b('HDL', 65),
      b('LDL', 95),
    ],
    expectedProducts: [
      'supp-vitamin-d3-k2',
      'supp-b-complex-methylated',
      'supp-iron-bisglycinate',
      'supp-magnesium-glycinate',
      'supp-omega-3-fish-oil',
    ],
    forbiddenProducts: [],
    drCPlan: {
      products: ['supp-vitamin-d3-k2', 'supp-b-complex-methylated', 'supp-iron-bisglycinate', 'supp-magnesium-glycinate', 'supp-omega-3-fish-oil'],
      dosing: [
        { productId: 'supp-vitamin-d3-k2', dosage: '5000 IU D3 + 100mcg K2 daily' },
        { productId: 'supp-b-complex-methylated', dosage: '1 capsule daily with food' },
        { productId: 'supp-iron-bisglycinate', dosage: '25mg elemental iron daily, away from calcium' },
        { productId: 'supp-magnesium-glycinate', dosage: '400mg before bed' },
        { productId: 'supp-omega-3-fish-oil', dosage: '2g EPA/DHA daily with meal' },
      ],
      labOrders: ['Vitamin D (25-OH)', 'B12', 'Ferritin', 'hs-CRP', 'CBC'],
      monitoringTimeline: 'Recheck labs at 90 days. If D and ferritin normalizing, maintain. If CRP still elevated, add curcumin.',
    },
  },

  // ─── Case 2: Female hormone suboptimal ───
  {
    caseId: 'gs-002-marianna',
    name: 'Marianna — Female Hormone Suboptimal',
    description: '42F with sluggish thyroid, low progesterone, high cortisol, low DHEA-S.',
    demographics: { age: 42, sex: 'female' },
    goals: ['hormonal', 'energy', 'sleep'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('Vitamin D (25-OH)', 35),
      b('TSH', 3.8),
      b('Free T3', 2.4),
      b('Free T4', 1.0),
      b('Progesterone (day 21)', 4.2, 'female'),
      b('DHEA-S', 85, 'female'),
      b('Cortisol (AM)', 19),
      b('Ferritin', 55, 'female'),
      b('Fasting Glucose', 78),
      b('hs-CRP', 0.4),
    ],
    expectedProducts: [
      'supp-vitamin-d3-k2',
      'supp-selenium',
      'supp-evening-primrose-oil',
      'supp-ashwagandha-ksm66',
    ],
    forbiddenProducts: [],
    drCPlan: {
      products: ['supp-vitamin-d3-k2', 'supp-selenium', 'supp-evening-primrose-oil', 'supp-ashwagandha-ksm66'],
      dosing: [
        { productId: 'supp-vitamin-d3-k2', dosage: '5000 IU D3 + 100mcg K2 daily' },
        { productId: 'supp-selenium', dosage: '200mcg daily' },
        { productId: 'supp-evening-primrose-oil', dosage: '1300mg daily' },
        { productId: 'supp-ashwagandha-ksm66', dosage: '600mg daily' },
      ],
      labOrders: ['TSH', 'Free T3', 'Free T4', 'Progesterone (day 21)', 'DHEA-S', 'Cortisol (AM)'],
      monitoringTimeline: 'Recheck thyroid + hormones at 60 days. If TSH not improving, consider low-dose levothyroxine referral.',
    },
  },

  // ─── Case 3: Male metabolic syndrome ───
  {
    caseId: 'gs-003-marcus',
    name: 'Marcus — Male Metabolic Syndrome',
    description: '52M with insulin resistance, high triglycerides, systemic inflammation.',
    demographics: { age: 52, sex: 'male' },
    goals: ['body_composition', 'cardiovascular'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('Fasting Glucose', 105),
      b('Fasting Insulin', 15.2),
      b('Hemoglobin A1c', 5.8),
      b('Triglycerides', 185),
      b('HDL', 38),
      b('hs-CRP', 2.5),
      b('Total Cholesterol', 225),
      b('LDL', 145),
      b('Total Testosterone', 480, 'male'),
      b('Vitamin D (25-OH)', 42),
    ],
    expectedProducts: [
      'supp-berberine',
      'supp-omega-3-fish-oil',
      'supp-alpha-lipoic-acid',
      'supp-chromium-picolinate',
    ],
    forbiddenProducts: [],
    drCPlan: {
      products: ['supp-berberine', 'supp-omega-3-fish-oil', 'supp-alpha-lipoic-acid', 'supp-chromium-picolinate'],
      dosing: [
        { productId: 'supp-berberine', dosage: '500mg twice daily with meals' },
        { productId: 'supp-omega-3-fish-oil', dosage: '3g EPA/DHA daily' },
        { productId: 'supp-alpha-lipoic-acid', dosage: '600mg daily' },
        { productId: 'supp-chromium-picolinate', dosage: '500mcg daily' },
      ],
      labOrders: ['Fasting Glucose', 'Fasting Insulin', 'Hemoglobin A1c', 'Lipid Panel', 'hs-CRP'],
      monitoringTimeline: 'Recheck metabolic panel at 90 days. Target A1c <5.5, triglycerides <150, fasting insulin <8.',
    },
  },

  // ─── Case 4: All optimal (negative control) ───
  {
    caseId: 'gs-004-olivia',
    name: 'Optimal Olivia — Negative Control',
    description: '35F with all biomarkers in functional optimal range. Minimal recommendations expected.',
    demographics: { age: 35, sex: 'female' },
    goals: ['longevity'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('Vitamin D (25-OH)', 62),
      b('B12', 600),
      b('Ferritin', 70, 'female'),
      b('Magnesium (RBC)', 6.0),
      b('TSH', 1.4),
      b('Free T3', 3.5),
      b('Fasting Glucose', 80),
      b('Fasting Insulin', 3.8),
      b('Hemoglobin A1c', 5.0),
      b('hs-CRP', 0.3),
      b('HDL', 65),
      b('LDL', 95),
      b('Triglycerides', 65),
      b('Homocysteine', 7),
    ],
    // All optimal — model should recommend very few or no supplements
    expectedProducts: [],
    forbiddenProducts: [],
    drCPlan: {
      products: [],
      dosing: [],
      labOrders: ['CBC', 'CMP'],
      monitoringTimeline: 'Annual wellness labs. No intervention needed — all markers in functional optimal range.',
    },
  },

  // ─── Case 5: Male multi-nutritional deficiency ───
  {
    caseId: 'gs-005-iron-mike',
    name: 'Iron Mike — Male Multi-Nutritional',
    description: '60M with very low B12, low ferritin, low zinc, low folate, high homocysteine.',
    demographics: { age: 60, sex: 'male' },
    goals: ['energy', 'cognition'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('B12', 250),
      b('Ferritin', 45, 'male'),
      b('Zinc', 55),
      b('Folate', 4.5),
      b('Homocysteine', 16),
      b('Vitamin D (25-OH)', 48),
      b('TSH', 2.0),
      b('Fasting Glucose', 85),
      b('hs-CRP', 0.8),
      b('Hemoglobin', 13.2),
    ],
    expectedProducts: [
      'supp-b-complex-methylated',
      'supp-iron-bisglycinate',
      'supp-zinc-copper',
    ],
    forbiddenProducts: [],
    drCPlan: {
      products: ['supp-b-complex-methylated', 'supp-iron-bisglycinate', 'supp-zinc-copper'],
      dosing: [
        { productId: 'supp-b-complex-methylated', dosage: '1 capsule daily with food' },
        { productId: 'supp-iron-bisglycinate', dosage: '25mg elemental iron daily' },
        { productId: 'supp-zinc-copper', dosage: '30mg zinc + 2mg copper daily' },
      ],
      labOrders: ['B12', 'Folate', 'Ferritin', 'Homocysteine', 'CBC'],
      monitoringTimeline: 'Recheck at 60 days. Priority: homocysteine normalization and B12 repletion. If homocysteine >12 at recheck, add methylfolate.',
    },
  },

  // ─── Case 6: Chronic inflammation ───
  {
    caseId: 'gs-006-irene',
    name: 'Inflamed Irene — Chronic Inflammation',
    description: '48F with critical hs-CRP, elevated ESR, high homocysteine, liver stress.',
    demographics: { age: 48, sex: 'female' },
    goals: ['immune', 'cardiovascular'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('hs-CRP', 4.5),
      b('ESR', 25),
      b('Homocysteine', 18),
      b('Triglycerides', 160),
      b('ALT', 35),
      b('GGT', 45),
      b('Vitamin D (25-OH)', 40),
      b('Ferritin', 80, 'female'),
      b('TSH', 1.8),
      b('Fasting Glucose', 88),
    ],
    expectedProducts: [
      'supp-omega-3-fish-oil',
      'supp-curcumin-turmeric',
      'supp-b-complex-methylated',
      'supp-nac',
    ],
    forbiddenProducts: [],
    drCPlan: {
      products: ['supp-omega-3-fish-oil', 'supp-curcumin-turmeric', 'supp-b-complex-methylated', 'supp-nac'],
      dosing: [
        { productId: 'supp-omega-3-fish-oil', dosage: '3g EPA/DHA daily' },
        { productId: 'supp-curcumin-turmeric', dosage: '1000mg curcumin with piperine daily' },
        { productId: 'supp-b-complex-methylated', dosage: '1 capsule daily' },
        { productId: 'supp-nac', dosage: '600mg twice daily' },
      ],
      labOrders: ['hs-CRP', 'ESR', 'Homocysteine', 'ALT', 'GGT', 'Ferritin'],
      monitoringTimeline: 'Recheck inflammatory markers at 60 days. If CRP still >3.0, escalate to provider for workup. Liver panel recheck at 90 days.',
    },
  },

  // ─── Case 7: Subclinical thyroid + low testosterone ───
  {
    caseId: 'gs-007-thyroid-tom',
    name: 'Thyroid Tom — Subclinical Thyroid',
    description: '55M with elevated TSH, low T3, high reverse T3, high TPO, low testosterone.',
    demographics: { age: 55, sex: 'male' },
    goals: ['energy', 'body_composition'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('TSH', 4.2),
      b('Free T3', 2.2),
      b('Free T4', 0.9),
      b('Reverse T3', 22),
      b('TPO Antibodies', 45),
      b('Total Testosterone', 350, 'male'),
      b('Free Testosterone', 10.0, 'male'),
      b('Zinc', 62),
      b('Vitamin D (25-OH)', 38),
      b('hs-CRP', 1.2),
    ],
    expectedProducts: [
      'supp-selenium',
      'supp-tongkat-ali',
      'supp-zinc-copper',
      'supp-vitamin-d3-k2',
      'supp-ashwagandha-ksm66',
    ],
    forbiddenProducts: [],
    drCPlan: {
      products: ['supp-selenium', 'supp-tongkat-ali', 'supp-zinc-copper', 'supp-vitamin-d3-k2', 'supp-ashwagandha-ksm66'],
      dosing: [
        { productId: 'supp-selenium', dosage: '200mcg daily' },
        { productId: 'supp-tongkat-ali', dosage: '400mg daily, cycle 5 days on / 2 off' },
        { productId: 'supp-zinc-copper', dosage: '30mg zinc + 2mg copper daily' },
        { productId: 'supp-vitamin-d3-k2', dosage: '5000 IU D3 + 100mcg K2 daily' },
        { productId: 'supp-ashwagandha-ksm66', dosage: '600mg daily' },
      ],
      labOrders: ['TSH', 'Free T3', 'Free T4', 'TPO Antibodies', 'Total Testosterone', 'Free Testosterone', 'Vitamin D (25-OH)'],
      monitoringTimeline: 'Recheck thyroid + testosterone at 90 days. If TSH still >3.5, consider levothyroxine. If testosterone <400, consider TRT consultation.',
    },
  },

  // ─── Case 8: Drug interaction safety test ───
  {
    caseId: 'gs-008-safety-sarah',
    name: 'Safety Sarah — SSRI Interaction Test',
    description: '40F on sertraline (SSRI). Tests that safety checker blocks dangerous interactions.',
    demographics: { age: 40, sex: 'female' },
    goals: ['energy'],
    medications: ['sertraline'],
    allergies: [],
    biomarkers: [
      b('Vitamin D (25-OH)', 25),
      b('Magnesium (RBC)', 4.5),
      b('hs-CRP', 1.5),
      b('B12', 320),
      b('Ferritin', 30, 'female'),
      b('TSH', 2.2),
      b('Fasting Glucose', 80),
      b('Hemoglobin A1c', 5.1),
    ],
    expectedProducts: [
      'supp-vitamin-d3-k2',
      'supp-magnesium-glycinate',
      'supp-omega-3-fish-oil',
      'supp-b-complex-methylated',
    ],
    // SSRI + Methylene Blue = serotonin syndrome (critical safety rule)
    forbiddenProducts: ['lon-methylene-blue'],
    drCPlan: {
      products: ['supp-vitamin-d3-k2', 'supp-magnesium-glycinate', 'supp-omega-3-fish-oil', 'supp-b-complex-methylated'],
      dosing: [
        { productId: 'supp-vitamin-d3-k2', dosage: '5000 IU D3 + 100mcg K2 daily' },
        { productId: 'supp-magnesium-glycinate', dosage: '400mg before bed' },
        { productId: 'supp-omega-3-fish-oil', dosage: '2g EPA/DHA daily' },
        { productId: 'supp-b-complex-methylated', dosage: '1 capsule daily with food' },
      ],
      labOrders: ['Vitamin D (25-OH)', 'Magnesium (RBC)', 'B12', 'Ferritin', 'hs-CRP'],
      monitoringTimeline: 'Recheck at 90 days. SSRI interaction check on every visit. NEVER prescribe methylene blue while on sertraline.',
    },
  },

  // ─── Case 9: Young male hormone optimization ───
  {
    caseId: 'gs-009-young-adam',
    name: 'Young Adam — Male Hormone Optimization',
    description: '38M with low testosterone, low free T, high SHBG, low zinc and vitamin D.',
    demographics: { age: 38, sex: 'male' },
    goals: ['hormonal', 'energy', 'body_composition'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('Total Testosterone', 320, 'male'),
      b('Free Testosterone', 9.5, 'male'),
      b('SHBG', 55, 'male'),
      b('Zinc', 65),
      b('Vitamin D (25-OH)', 32),
      b('Magnesium (RBC)', 5.2),
      b('Fasting Glucose', 82),
      b('hs-CRP', 0.6),
      b('TSH', 1.6),
      b('Cortisol (AM)', 14),
    ],
    expectedProducts: [
      'supp-tongkat-ali',
      'supp-boron',
      'supp-zinc-copper',
      'supp-vitamin-d3-k2',
      'supp-ashwagandha-ksm66',
    ],
    forbiddenProducts: [],
    drCPlan: {
      products: ['supp-tongkat-ali', 'supp-boron', 'supp-zinc-copper', 'supp-vitamin-d3-k2', 'supp-ashwagandha-ksm66'],
      dosing: [
        { productId: 'supp-tongkat-ali', dosage: '400mg daily, cycle 5 days on / 2 off' },
        { productId: 'supp-boron', dosage: '6mg daily' },
        { productId: 'supp-zinc-copper', dosage: '30mg zinc + 2mg copper daily' },
        { productId: 'supp-vitamin-d3-k2', dosage: '5000 IU D3 + 100mcg K2 daily' },
        { productId: 'supp-ashwagandha-ksm66', dosage: '600mg daily' },
      ],
      labOrders: ['Total Testosterone', 'Free Testosterone', 'SHBG', 'Zinc', 'Vitamin D (25-OH)', 'Estradiol'],
      monitoringTimeline: 'Recheck testosterone panel + SHBG at 90 days. If free T still <12, consider TRT referral. Monitor estradiol to ensure no aromatization.',
    },
  },

  // ─── Case 10: Multi-system — comprehensive ───
  {
    caseId: 'gs-010-complete-carol',
    name: 'Complete Carol — Multi-System',
    description: '50F with deficiencies across nutritional, metabolic, thyroid, and inflammatory markers.',
    demographics: { age: 50, sex: 'female' },
    goals: ['longevity', 'energy', 'cardiovascular', 'sleep'],
    medications: [],
    allergies: [],
    biomarkers: [
      b('Vitamin D (25-OH)', 32),
      b('hs-CRP', 2.1),
      b('Fasting Insulin', 8.5),
      b('TSH', 3.5),
      b('Free T3', 2.6),
      b('Magnesium (RBC)', 4.5),
      b('B12', 380),
      b('Ferritin', 28, 'female'),
      b('Fasting Glucose', 92),
      b('Hemoglobin A1c', 5.4),
      b('Triglycerides', 120),
      b('Homocysteine', 12),
    ],
    expectedProducts: [
      'supp-vitamin-d3-k2',
      'supp-omega-3-fish-oil',
      'supp-berberine',
      'supp-selenium',
      'supp-magnesium-glycinate',
      'supp-b-complex-methylated',
      'supp-iron-bisglycinate',
    ],
    forbiddenProducts: [],
    drCPlan: {
      products: ['supp-vitamin-d3-k2', 'supp-omega-3-fish-oil', 'supp-berberine', 'supp-selenium', 'supp-magnesium-glycinate', 'supp-b-complex-methylated', 'supp-iron-bisglycinate'],
      dosing: [
        { productId: 'supp-vitamin-d3-k2', dosage: '5000 IU D3 + 100mcg K2 daily' },
        { productId: 'supp-omega-3-fish-oil', dosage: '2g EPA/DHA daily' },
        { productId: 'supp-berberine', dosage: '500mg twice daily with meals' },
        { productId: 'supp-selenium', dosage: '200mcg daily' },
        { productId: 'supp-magnesium-glycinate', dosage: '400mg before bed' },
        { productId: 'supp-b-complex-methylated', dosage: '1 capsule daily with food' },
        { productId: 'supp-iron-bisglycinate', dosage: '25mg elemental iron daily, away from calcium' },
      ],
      labOrders: ['TSH', 'Free T3', 'Vitamin D (25-OH)', 'Fasting Insulin', 'hs-CRP', 'Ferritin', 'Homocysteine', 'Hemoglobin A1c'],
      monitoringTimeline: 'Recheck comprehensive panel at 90 days. Priority: thyroid + insulin sensitivity. If A1c trending up, consider metformin referral.',
    },
  },
];
